<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h4 class="mb-0">Logs</h4>
      <b-button
        variant="flat-primary"
        size="sm"
        class="btn-icon"
        :disabled="loadingLogs"
        v-b-tooltip.hover
        title="Actualizar Logs"
        @click="refresh(logsPagination.currentPage)"
      >
        <feather-icon icon="RefreshCwIcon" size="16"/>
      </b-button>
    </div>

    <div v-if="loadingLogs" class="text-center my-3">
      <b-spinner variant="primary" small></b-spinner>
    </div>
    <template v-else>
      <div v-if="logsData.length > 0">
        <logs :logs="logsData"/>

        <div class="d-flex flex-wrap align-items-center justify-content-between mt-1">
          <small class="text-muted">
            Mostrando {{ logsPagination.from }} a {{ logsPagination.to }} de {{ logsPagination.total }} logs
          </small>

          <b-pagination
            v-if="logsPagination.lastPage > 1"
            :value="logsPagination.currentPage"
            :total-rows="logsPagination.total"
            :per-page="logsPagination.max"
            size="md"
            class="mb-0"
            @input="onPageChange"
          />
        </div>
      </div>
      <b-alert
        v-else
        show
        variant="warning"
        class="p-1 mb-0"
      >
        No hay logs existentes
      </b-alert>
    </template>
  </b-card>
</template>

<script>
import {onMounted, ref, watch} from '@vue/composition-api';
import {useToast} from 'vue-toastification/composition';
import axios from '@/libs/axios';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Logs from './Logs.vue';

export default {
  name: 'LogsSection',
  components: {
    Logs,
  },
  props: {
    crisisId: {
      type: [Number, String],
      default: null,
    },
  },
  setup(props, {emit}) {
    const toast = useToast();
    const loadingLogs = ref(false);
    const logsData = ref([]);
    const logsPagination = ref({
      currentPage: 1,
      lastPage: 1,
      max: 15,
      total: 0,
      from: 0,
      to: 0,
    });

    onMounted(() => {
      refresh(1);
    });

    watch(
      () => props.crisisId,
      (crisisId, previousCrisisId) => {
        if (crisisId && crisisId !== previousCrisisId) {
          refresh(1);
        }
      }
    );

    const emitLatestLogDate = () => {
      emit('latest-log-date', logsData.value[0]?.created_at || null);
    };

    const applyLogsResponse = (payload) => {
      if (Array.isArray(payload)) {
        logsData.value = payload;
        logsPagination.value = {
          currentPage: 1,
          lastPage: 1,
          max: payload.length || 15,
          total: payload.length,
          from: payload.length ? 1 : 0,
          to: payload.length,
        };
        emitLatestLogDate();
        return;
      }

      const data = Array.isArray(payload?.data) ? payload.data : [];
      logsData.value = data;
      logsPagination.value = {
        currentPage: payload?.current_page || 1,
        lastPage: payload?.last_page || 1,
        max: Number(payload?.per_page) || data.length || 15,
        total: payload?.total || data.length,
        from: payload?.from || (data.length ? 1 : 0),
        to: payload?.to || data.length,
      };
      emitLatestLogDate();
    };

    const refresh = async (page = 1) => {
      if (!props.crisisId) return;

      loadingLogs.value = true;
      await axios.get(`/v1/operational-security-crisis/${props.crisisId}/logs`, {
        params: {
          'page[n]': page,
        },
      })
        .then(response => {
          applyLogsResponse(response.data);
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error al obtener los logs',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          });
        })
        .finally(() => {
          loadingLogs.value = false;
        });
    };

    const onPageChange = (page) => {
      if (page === logsPagination.value.currentPage || loadingLogs.value) return;

      refresh(page);
    };

    return {
      loadingLogs,
      logsData,
      logsPagination,
      onPageChange,
      refresh,
    };
  },
};
</script>
